import { useArrayFind, useArrayReduce } from '@vueuse/core';
import { useRouteQuery } from '@vueuse/router';
import { object, array, string, number, union, literal } from 'zod';
import type { z } from 'zod';

import { useCurrentSignatureRequestKey } from '~~core';

import { buildWidgetURL, minifySignatureConfig } from '../../utils/serial-signature';

export type SerialSignature = z.infer<typeof signatureSchema>;
export type SerialSignatureConfig = z.infer<typeof configSchema>;

const defaultStorageData = {
  signatures: [],
  documentsSignedCount: 0,
};

/**
 * The schemas on this file are using 1 char characters to reduce the size of the data
 * that is stored in the cookie. This is because the cookie has a limit of 4kb and we
 * need to store the data of the signatures that are pending to be signed.
 *
 * The reason why cookies are used instead of localStorage is to keep the solution cross-domain
 * compatible since on dev environment the widget is served from a different domain than the
 * main app. And it's not a good idea to bind the two apps to the same domain thinking ont the
 * future.
 */
const signatureSchema = object({
  k: string(),
  c: number(),
  t: union([literal('envelope'), literal('batch')]),
}).transform((data) => ({
  key: data.k,
  type: data.t,
  signableDocumentsCount: data.c,
}));

const configSchema = object({
  s: array(signatureSchema).default([]),
  c: number().default(0),
}).transform((data) => ({
  signatures: data.s,
  documentsSignedCount: data.c,
})).catch(defaultStorageData);

export function useSerialSignature() {
  const { signatureRequestKey } = useCurrentSignatureRequestKey();
  const isSerialSignature = useRouteQuery('serial_signature', 'false', { transform: (val: unknown) => val === 'true' });
  const isFromSerialSignature = useRouteQuery('from_serial_signature', 'hey', { transform: (val: unknown) => val === 'true' });
  const storageData = useCookie<unknown>('serial_signature_data', { default: () => defaultStorageData });
  const parsedConfig = computed(() => configSchema.parse(storageData.value));

  const current = useArrayFind(() => parsedConfig.value.signatures, (signature) => signature.key === signatureRequestKey.value);
  const pendingSignatures = computed(() => parsedConfig.value.signatures);
  const totalToSign = useArrayReduce(pendingSignatures, (acc, { signableDocumentsCount }) => acc + signableDocumentsCount, () => parsedConfig.value.documentsSignedCount);
  const totalSigned = computed(() => parsedConfig.value.documentsSignedCount);
  const isInSerialSignature = computed(() => isSerialSignature.value && !!current.value && pendingSignatures.value.length > 0);
  const isInLastSignature = computed(() => pendingSignatures.value.length === 1);

  function setConfig(newConfig: SerialSignatureConfig) {
    storageData.value = minifySignatureConfig(newConfig);
  }

  function clearSignatures() {
    setConfig(defaultStorageData);
  }

  async function goToNextSignature() {
    const remainingSignatures = pendingSignatures.value.filter((signature) => signature !== current.value);
    const currentData = parsedConfig.value;
    const [nextSignature] = remainingSignatures;

    if (!nextSignature) return;

    const url = buildWidgetURL(nextSignature);

    setConfig({
      signatures: remainingSignatures,
      documentsSignedCount: currentData.documentsSignedCount + (current.value?.signableDocumentsCount ?? 0),
    });

    try {
      url.searchParams.set('from_serial_signature', 'true');
      await navigateTo(url.toString(), { external: true });
    } catch (err) {
      setConfig(currentData);

      throw err;
    }
  }

  function subtractDocsFromSerialSignature(n: number) {
    function transformSignature(signature: SerialSignature): SerialSignature {
      if (signature.key !== current.value?.key) return signature;

      return {
        ...signature,
        signableDocumentsCount: signature.signableDocumentsCount - n,
      };
    }

    setConfig({
      ...parsedConfig.value,
      signatures: parsedConfig.value.signatures.map(transformSignature),
    });
  }

  return {
    pendingSignatures: readonly(pendingSignatures),
    currentSerialDoc: readonly(current),
    isFromSerialSignature,
    isInLastSignature,
    totalToSign,
    totalSigned,
    isInSerialSignature,
    goToNextSignature,
    clearSignatures,
    subtractDocsFromSerialSignature,
  };
}
